import { Injectable, OnDestroy } from '@angular/core';
import gql from 'graphql-tag';
import {
  distinctUntilChanged,
  filter,
  map,
  scan,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import { CurrentUserGQL } from '@app/graphql/generated';
import { EventService } from '@app/event/event.service';
import { FormBuilder } from '@angular/forms';
import { UserService } from '@app/user/user.service';
import { ModalService } from '@app/shared/modal/modal.service';
import { RoutingService } from '@app/routing.service';
import { ActivatedRoute } from '@angular/router';
import { ResponsiveService } from '@app/utility/responsive.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@app/auth/auth.service';
import { config } from '@app/app.config';

interface EventTranscoders {
  eventId: any;
  event: any;
}

export interface Encoder {
  id: number,
  uuid: string,
  name: string,
  type: number,
  ip: string,
  port: string
}

const _state: EventTranscoders = {
  eventId: null,
  event: {
    transcoders: []
  }
};

@Injectable({
  providedIn: 'root'
})
export class EventTranscodersService implements OnDestroy{
  private store = new BehaviorSubject<EventTranscoders>(_state);
  private state$ = this.store.asObservable();
  private refetchEvent$ = new BehaviorSubject<void>(null);

  event = this.state$.pipe(
    map(state => state.event),
  );

  eventId = this.state$.pipe(
    map(state => state.eventId),
    distinctUntilChanged()
  );

  constructor(
    private currentUserGQL: CurrentUserGQL,
    public eventService: EventService,
    private fb: FormBuilder,
    private userService: UserService,
    private appModalService: ModalService,
    private routingService: RoutingService,
    private route: ActivatedRoute,
    public responsiveService: ResponsiveService,
    private apollo: Apollo,
    private http: HttpClient,
    private authService: AuthService
  ) {
    combineLatest([this.eventId, this.refetchEvent$])
      .pipe(
        filter(([eventId]) => Boolean(eventId)),
        switchMap(([eventId]) => this.fetchEvent(eventId)),
        switchMap(event => {
          const encoders = event.transcoders;

          event.transcoders = encoders.map(encoder => {
            const livePreview$ = this.subscribeToPreviewText(encoder.uuid).pipe(
              map(res =>
                res && res.data && res.data.encoderPreview
                  ? res.data.encoderPreview
                  : ''
              ),
              startWith('')
            );
            const encoderOutput$ = this.subscribeToEncoderOutput(
              encoder.uuid
            ).pipe(
              map(res =>
                res && res.data && res.data.encoderOutput
                  ? res.data.encoderOutput
                  : ''
              ),
              tap((res) => {

              }),
              startWith('')
            );
            return { ...encoder, livePreview$, encoderOutput$ };
          });

          return of(event);
        })
      )
      .subscribe(event => this.store.next({ ...this.store.value, event }));
  }

  private fetchEvent = eventId => {
    const eventGql = gql`
      query event($input: SingleEventInput!) {
        event(input: $input) {
          shortId
          title

          transcoders {
            uuid
            port
            ip
            enabled
            language
            encoder {
              uuid
            }
          }
        }
      }
    `;

    return this.apollo
      .query({
        query: eventGql,
        variables: { input: { shortId: eventId } },
        fetchPolicy: 'network-only'
      })
      .pipe(
        filter(response => !response.loading),
        // @ts-ignore
        map(response => response.data.event as any),
        map((e: any) => {
          const event = e;
          return event;
        })
      );
  };

  private subscribeToPreviewText = encoderId => {
    const subscription = gql`
      subscription encoderPreview($input: EncoderPreviewInput!) {
        encoderPreview(input: $input)
      }
    `;

    return this.apollo.subscribe<{ encoderPreview: string }>({
      query: subscription,
      variables: { input: { id: encoderId } }
    });
  };

  private subscribeToEncoderOutput = encoderId => {
    const subscription = gql`
      subscription encoderOutput($input: EncoderOutputInput!) {
        encoderOutput(input: $input)
      }
    `;

    return this.apollo.subscribe<{ encoderOutput: string }>({
      query: subscription,
      variables: { input: { id: encoderId } }
    });
  };

  public createEncoder = () => {
    const addTranscoder = gql`
      mutation patchEventEncoder($input: EncoderInput!) {
        patchEventEncoder(input: $input)
      }
    `;

    this.apollo
      .mutate({
        mutation: addTranscoder,
        variables: {
          input: {
            eventEncoderId: null,
            eventId: this.store.value.event.shortId,
            encoderId: null,
            language: 'en',
            enabled: false
          }
        }
      })
      .subscribe(() => this.refetchEvent());
  };

  updateEncoder = encoder => {
    const addTranscoder = gql`
      mutation patchEventEncoder($input: EncoderInput!) {
        patchEventEncoder(input: $input)
      }
    `;

    const variables = {
      input: {
        eventEncoderId: encoder.eventEncoderId,
        eventId: this.store.value.event.shortId,
        encoderId: encoder.encoderId,
        language: encoder.language,
        enabled: encoder.enabled
      }
    };

    return this.apollo
      .mutate({
        mutation: addTranscoder,
        variables
      })
      .subscribe(() => this.refetchEvent());
  };

  removeEncoder(id) {
    const removeTranscoderMutation = gql`
      mutation deleteEventEncoder($input: DeleteUserEncoderInput!) {
        deleteEventEncoder(input: $input)
      }
    `;
    this.apollo
      .mutate({
        mutation: removeTranscoderMutation,
        variables:
          {
            input: {
              id: id,
              shortId: this.store.value.event.shortId
            }
          }
      })
      .subscribe(() => {
        this.refetchEvent();
      });
  }

  public updateEventId(eventId) {
    this.store.next({ ...this.store.value, eventId });
  }

  public refetchEvent() {
    this.refetchEvent$.next();
  }

  public getUserEncoders(): Observable<Encoder[]> {
    return this.http
        .get<Encoder[]>(
            config.httpURL + `/api/transcoders`,
            { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }}
        );
  }

  public createUserEncoder(encoder: Encoder) {
    return this.http
      .post<Encoder>(
        config.httpURL + `/api/transcoders`,
        encoder,
        { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }}
      );
  }

  ngOnDestroy(): void {
  }
}
